// src/components/FamilyList.js

/**
 * Component to display a list of families and their members,
 * allowing editing/deleting of members and deleting families based on user permissions.
 * Modernized with search, overflow menus, and responsive design.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHome, faPhone, faGift, faPencilAlt,
    faTrashAlt, faPlus, faFilePdf, faSpinner, faUsers,
    faSearch, faEllipsisV, faFileImport, faFileCsv
} from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Dropdown, Modal, Button } from 'react-bootstrap';

import { db, storage, functions } from '../firebase';

import { collection, getDocs, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';

import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';
import FormattedPhoneNumber from './FormattedPhoneNumber';

import './FamilyList.css';

// --- Callable Function References ---
const deleteMemberCallable = httpsCallable(functions, 'deleteMember');
const deleteFamilyCallable = httpsCallable(functions, 'deleteFamily');
const generateDirectoryPdfCallable = httpsCallable(functions, 'generateDirectoryPdf');
const generateDirectoryCsvCallable = httpsCallable(functions, 'generateDirectoryCsv');


// --- Main Component: FamilyList ---
function FamilyList() {
    const { currentUser, isAdmin, userOrgId, userFidClaim, loadingAuth } = useAuth();

    const [families, setFamilies] = useState([]);
    const [allMembers, setAllMembers] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isGeneratingCsv, setIsGeneratingCsv] = useState(false);
    const [pdfMessage, setPdfMessage] = useState({ type: '', text: '' });
    const [deletingFamilyId, setDeletingFamilyId] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });

    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('a-z'); // 'a-z', 'z-a', 'recent'
    const [showDeceased, setShowDeceased] = useState(false); // Admin toggle to show deceased members

    // --- Effect: Fetch Families & Members ---
    useEffect(() => {
        if (loadingAuth || !userOrgId) {
            if (!loadingAuth && currentUser && !userOrgId) {
                setError("Organization details missing from your account. Cannot load families.");
                console.error("FamilyList: OrgId missing from context after auth loaded.");
                setLoadingData(false);
            }
            if (!loadingAuth) {
                setLoadingData(false);
            }
            return;
        }

        setLoadingData(true);
        setError(null);
        let isMounted = true;

        const fetchData = async () => {
            try {
                const familiesQuery = query(collection(db, 'family'), where('orgId', '==', userOrgId), orderBy('surname'));
                const membersQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId));

                const [familySnapshot, memberSnapshot] = await Promise.all([
                    getDocs(familiesQuery),
                    getDocs(membersQuery)
                ]);

                if (!isMounted) return;

                const familyListPromises = familySnapshot.docs.map(async (doc) => {
                    const familyData = { id: doc.id, ...doc.data() };
                    let photoURL = '/assets/blank.png';
                    if (familyData.photo) {
                        try {
                            if (familyData.photo.startsWith('http')) {
                                photoURL = familyData.photo;
                            } else {
                                const photoRef = ref(storage, familyData.photo);
                                photoURL = await getDownloadURL(photoRef);
                            }
                        } catch (imageError) {
                            if (imageError.code !== 'storage/object-not-found') {
                                console.error(`Failed to get photo URL for ${familyData.photo}:`, imageError);
                            }
                        }
                    }
                    return { ...familyData, photoURL };
                });
                const resolvedFamilyList = await Promise.all(familyListPromises);

                if (!isMounted) return;
                setFamilies(resolvedFamilyList);

                const resolvedMemberList = memberSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

                if (!isMounted) return;
                setAllMembers(resolvedMemberList);

            } catch (err) {
                console.error('FamilyList: Error during data fetching:', err);
                if (isMounted) {
                    if (err.code === 'permission-denied') { setError("Permission denied. Check Firestore security rules."); }
                    else if (err.code === 'unauthenticated') { setError("Authentication error during data fetch. Please refresh or log in again."); }
                    else if (err.code === 'failed-precondition' && err.message?.includes('index')) { setError("Database indexing error. Please contact support."); console.error("Firestore requires an index:", err); }
                    else { setError(err.message || "An unknown error occurred while fetching data."); }
                }
            } finally {
                if (isMounted) { setLoadingData(false); }
            }
        };

        fetchData();

        return () => { isMounted = false; };
    }, [userOrgId, loadingAuth, currentUser]);


    // --- Memoized Calculation: Group Members by Family ID (with deceased filtering) ---
    const membersByFamilyId = useMemo(() => {
        const grouped = {};
        allMembers.forEach(member => {
            if (!member || !member.familyId) return;
            // Filter out deceased members unless showDeceased is enabled
            if (member.status === 'deceased' && !showDeceased) return;
            if (!grouped[member.familyId]) grouped[member.familyId] = [];
            grouped[member.familyId].push(member);
        });
        Object.values(grouped).forEach(memberList => {
            memberList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        });
        return grouped;
    }, [allMembers, showDeceased]);

    // Count deceased members for the toggle badge
    const deceasedCount = useMemo(() => {
        return allMembers.filter(m => m.status === 'deceased').length;
    }, [allMembers]);

    // --- Filtered and Sorted Families ---
    const filteredFamilies = useMemo(() => {
        let result = [...families];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(family => {
                // Search by family name
                if (family.surname?.toLowerCase().includes(query)) return true;
                // Search by member names
                const familyMembers = membersByFamilyId[family.id] || [];
                return familyMembers.some(member =>
                    member.name?.toLowerCase().includes(query) ||
                    member.email?.toLowerCase().includes(query)
                );
            });
        }

        // Apply sort
        switch (sortOrder) {
            case 'z-a':
                result.sort((a, b) => (b.surname || '').localeCompare(a.surname || ''));
                break;
            case 'recent':
                result.sort((a, b) => {
                    const aTime = a.createdAt?.toMillis?.() || 0;
                    const bTime = b.createdAt?.toMillis?.() || 0;
                    return bTime - aTime;
                });
                break;
            default: // 'a-z'
                result.sort((a, b) => (a.surname || '').localeCompare(b.surname || ''));
        }

        return result;
    }, [families, searchQuery, sortOrder, membersByFamilyId]);


    // --- Helper: Format Date ---
    const formatDate = (date) => {
        const formatString = 'MM/dd';
        if (date instanceof Timestamp) {
            try { return format(date.toDate(), formatString); }
            catch (e) { console.error("formatDate Error (Timestamp):", e); return '?'; }
        } else if (typeof date === 'string' && /^\d{2}\/\d{2}$/.test(date)) {
            return date;
        }
        return '';
    };

    // --- Handlers for Delete and PDF ---
    const openDeleteModal = (familyId, familyName) => {
        setDeleteModal({ show: true, id: familyId, name: familyName });
    };

    const closeDeleteModal = () => {
        if (!deletingFamilyId) {
            setDeleteModal({ show: false, id: null, name: '' });
        }
    };

    const handleConfirmDeleteFamily = async () => {
        const { id: familyIdToDelete, name: familyName } = deleteModal;
        setError(null); setPdfMessage({ type: '', text: '' });
        setDeletingFamilyId(familyIdToDelete);
        try {
            const result = await deleteFamilyCallable({ familyId: familyIdToDelete });
            if (result?.data?.success) {
                setFamilies(prevFamilies => prevFamilies.filter(family => family.id !== familyIdToDelete));
                setAllMembers(prevMembers => prevMembers.filter(member => member.familyId !== familyIdToDelete));
                setDeleteModal({ show: false, id: null, name: '' });
                setPdfMessage({ type: 'success', text: `Family "${familyName}" deleted.` });
                setTimeout(() => setPdfMessage({ type: '', text: '' }), 5000);
            } else {
                setError(result?.data?.message || "Server denied the family deletion request.");
            }
        } catch (err) { setError(`Failed to delete family: ${err.message || "Network/Function error"}`); }
        finally { setDeletingFamilyId(null); }
    };

    const handleGeneratePdf = async () => {
        console.log("Attempting to generate PDF directory...");
        setIsGeneratingPdf(true); setPdfMessage({ type: '', text: '' }); setError(null);
        try {
            const result = await generateDirectoryPdfCallable({});
            const data = result.data;
            if (data?.success && data.filePath) {
                setPdfMessage({ type: 'info', text: 'PDF generated. Preparing download...' });
                const bucketName = data.filePath.split('/')[2];
                const relativePath = data.filePath.substring(`gs://${bucketName}/`.length);
                const storageRef = ref(storage, relativePath);
                const downloadUrl = await getDownloadURL(storageRef);
                const link = document.createElement('a'); link.href = downloadUrl; link.download = 'directory.pdf';
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
                setPdfMessage({ type: 'success', text: 'PDF download started!' }); setTimeout(() => setPdfMessage({ type: '', text: '' }), 5000);
            } else if (data?.success && !data.filePath) {
                setPdfMessage({ type: 'warning', text: 'Directory generation complete, but no families or members were found to include.' });
            } else {
                setPdfMessage({ type: 'danger', text: `Error: ${data?.message || "PDF generation failed on the server."}` });
            }
        } catch (err) { setPdfMessage({ type: 'danger', text: `Error: ${err.message || "Could not generate PDF."}` }); }
        finally { setIsGeneratingPdf(false); }
    };

    const handleGenerateCsv = async () => {
        setIsGeneratingCsv(true);
        setPdfMessage({ type: '', text: '' });
        setError(null);
        try {
            const result = await generateDirectoryCsvCallable({});
            const data = result.data;
            if (data?.success && data.csv) {
                const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = data.filename || 'directory-export.csv';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                setPdfMessage({ type: 'success', text: 'CSV download started!' });
                setTimeout(() => setPdfMessage({ type: '', text: '' }), 5000);
            } else if (data?.success && !data.csv) {
                setPdfMessage({ type: 'warning', text: 'Export complete, but no families or members were found to include.' });
            } else {
                setPdfMessage({ type: 'danger', text: `Error: ${data?.message || "CSV generation failed."}` });
            }
        } catch (err) {
            setPdfMessage({ type: 'danger', text: `Error: ${err.message || "Could not generate CSV export."}` });
        } finally {
            setIsGeneratingCsv(false);
        }
    };


    // --- Render Logic ---
    if (loadingAuth) {
        return (
            <div className="family-list-page">
                <div className="family-list-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-search"></div>
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                </div>
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="container mt-3">
                <Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view families.</Alert>
            </div>
        );
    }

    if (loadingData) {
        return (
            <div className="family-list-page">
                <div className="family-list-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-search"></div>
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-3">
                <Alert variant="danger">
                    <h4>Error Loading Data</h4>
                    <p>{error}</p>
                </Alert>
            </div>
        );
    }

    return (
        <div className="family-list-page">
            {/* Page Header */}
            <div className="family-list-header">
                <div className="container">
                    <div className="header-content">
                        <div className="header-title-section">
                            <h1 className="header-title">
                                <FontAwesomeIcon icon={faUsers} className="header-icon" />
                                Church Directory
                            </h1>
                            <span className="family-count-badge">
                                {families.length} {families.length === 1 ? 'Family' : 'Families'}
                            </span>
                        </div>
                        <div className="header-actions">
                            {isAdmin && (
                                <Link to="/import-directory" className="btn-header-action btn-pdf">
                                    <FontAwesomeIcon icon={faFileImport} />
                                    <span className="btn-text">Import Members</span>
                                </Link>
                            )}
                            {isAdmin && (
                                <button
                                    type="button"
                                    className="btn-header-action btn-pdf"
                                    onClick={handleGeneratePdf}
                                    disabled={isGeneratingPdf || isGeneratingCsv || !userOrgId}
                                    title={isGeneratingPdf ? "Generating PDF..." : "Download Directory PDF"}
                                >
                                    {isGeneratingPdf ? (
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    ) : (
                                        <FontAwesomeIcon icon={faFilePdf} />
                                    )}
                                    <span className="btn-text">
                                        {isGeneratingPdf ? "Generating..." : "Download PDF"}
                                    </span>
                                </button>
                            )}
                            {isAdmin && (
                                <button
                                    type="button"
                                    className="btn-header-action btn-pdf"
                                    onClick={handleGenerateCsv}
                                    disabled={isGeneratingCsv || isGeneratingPdf || !userOrgId}
                                    title={isGeneratingCsv ? "Generating CSV..." : "Download Directory CSV"}
                                >
                                    {isGeneratingCsv ? (
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    ) : (
                                        <FontAwesomeIcon icon={faFileCsv} />
                                    )}
                                    <span className="btn-text">
                                        {isGeneratingCsv ? "Generating..." : "Download CSV"}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Search and Filter Bar */}
                    <div className="search-filter-bar">
                        <div className="search-box">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search families or members..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            {searchQuery && (
                                <button
                                    className="search-clear"
                                    onClick={() => setSearchQuery('')}
                                    title="Clear search"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        <div className="filter-sort">
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="sort-select"
                            >
                                <option value="a-z">A → Z</option>
                                <option value="z-a">Z → A</option>
                                <option value="recent">Recently Added</option>
                            </select>
                            {/* Subtle deceased toggle for admins */}
                            {isAdmin && deceasedCount > 0 && (
                                <label className="deceased-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={showDeceased}
                                        onChange={(e) => setShowDeceased(e.target.checked)}
                                    />
                                    <span className="deceased-checkbox-label">
                                        Include Deceased ({deceasedCount})
                                    </span>
                                </label>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages */}
            <div className="container">
                {pdfMessage.text && (
                    <Alert
                        variant={pdfMessage.type || 'info'}
                        className="mt-3"
                        onClose={() => setPdfMessage({ type: '', text: '' })}
                        dismissible
                    >
                        {pdfMessage.text}
                    </Alert>
                )}
            </div>

            {/* Family List Content */}
            <div className="family-list-content">
                <div className="container">
                    {filteredFamilies.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <FontAwesomeIcon icon={faUsers} />
                            </div>
                            {searchQuery ? (
                                <>
                                    <h3>No families found</h3>
                                    <p>No families match "{searchQuery}". Try a different search term.</p>
                                    <button
                                        className="btn-clear-search"
                                        onClick={() => setSearchQuery('')}
                                    >
                                        Clear Search
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3>No families yet</h3>
                                    <p>Your church directory is empty.</p>
                                    {isAdmin && (
                                        <Link to="/families/add" className="btn-add-first-family">
                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                            Add Your First Family
                                        </Link>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {searchQuery && (
                                <div className="search-results-info">
                                    Found {filteredFamilies.length} {filteredFamilies.length === 1 ? 'family' : 'families'} matching "{searchQuery}"
                                </div>
                            )}

                            {filteredFamilies.map((family) => {
                                const familyMembers = membersByFamilyId[family.id] || [];
                                const canEditThisFamily = isAdmin || (userFidClaim && userFidClaim === family.id);

                                return (
                                    <div className="family-card" key={family.id} style={{ position: 'relative' }}>
                                        {deletingFamilyId === family.id && (
                                            <div className="deleting-overlay">
                                                <FontAwesomeIcon icon={faSpinner} spin className="deleting-overlay-spinner" />
                                                <span className="deleting-overlay-text">Deleting family...</span>
                                            </div>
                                        )}
                                        <div className="family-card-inner">
                                            {/* Family Info Section */}
                                            <div className="family-info-section">
                                                <div className="family-photo-wrapper">
                                                    <Link to={`/families/${family.id}`}>
                                                        <img
                                                            className="family-photo"
                                                            src={family.photoURL}
                                                            alt={`${family.surname || 'Family'} photo`}
                                                            loading="lazy"
                                                            onError={(e) => {
                                                                if (e.target.src !== '/assets/blank.png') {
                                                                    e.target.onerror = null;
                                                                    e.target.src='/assets/blank.png';
                                                                }
                                                            }}
                                                        />
                                                    </Link>
                                                </div>

                                                <div className="family-details">
                                                    <div className="family-name-row">
                                                        <Link to={`/families/${family.id}`} className="family-name-link">
                                                            <h3 className="family-name">{family.surname}</h3>
                                                        </Link>

                                                        <div className="family-name-actions">
                                                            {canEditThisFamily && (
                                                                <Link
                                                                    to="/members/add"
                                                                    state={{ preselectedFamilyId: family.id, preselectedFamilyName: family.surname }}
                                                                    className="btn-add-member"
                                                                >
                                                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                                                    Add Member
                                                                </Link>
                                                            )}

                                                            {/* Overflow Menu */}
                                                            {canEditThisFamily && (
                                                                <Dropdown align="end" className="family-menu">
                                                                    <Dropdown.Toggle as="button" className="family-menu-btn">
                                                                        <FontAwesomeIcon icon={faEllipsisV} />
                                                                    </Dropdown.Toggle>
                                                                    <Dropdown.Menu>
                                                                        <Dropdown.Item
                                                                            as={Link}
                                                                            to={`/families/edit/${family.id}`}
                                                                        >
                                                                            <FontAwesomeIcon icon={faPencilAlt} className="me-2" />
                                                                            Edit Family
                                                                        </Dropdown.Item>
                                                                        <Dropdown.Divider />
                                                                        <Dropdown.Item
                                                                            className="text-danger"
                                                                            onClick={() => openDeleteModal(family.id, family.surname)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                                                            Remove Family
                                                                        </Dropdown.Item>
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="family-contact-info">
                                                        {family.street && (
                                                            <div className="contact-item">
                                                                <FontAwesomeIcon icon={faHome} className="contact-icon" />
                                                                <span>{family.street}{family.city ? `, ${family.city}` : ''}{family.state ? `, ${family.state}` : ''} {family.zipcode}</span>
                                                            </div>
                                                        )}
                                                        {family.phone && (
                                                            <div className="contact-item">
                                                                <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                                                                <FormattedPhoneNumber phoneNumber={family.phone} fallback="" />
                                                            </div>
                                                        )}
                                                        {family.anniversary && (
                                                            <div className="contact-item">
                                                                <FontAwesomeIcon icon={faGift} className="contact-icon" />
                                                                <span>Anniversary: {formatDate(family.anniversary)}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Members Avatars */}
                                                    {familyMembers.length > 0 && (
                                                        <div className="members-avatars">
                                                            {familyMembers.map((member) => {
                                                                if (!member || !member.id) return null;

                                                                const photoCacheKey = member.imageUpdatedAt instanceof Timestamp
                                                                    ? member.imageUpdatedAt.toMillis().toString()
                                                                    : (member.imageUpdatedAt || member.id);

                                                                const isDeceased = member.status === 'deceased';

                                                                return (
                                                                    <Link
                                                                        key={member.id}
                                                                        to={`/families/${family.id}/member/${member.id}`}
                                                                        className={`member-avatar-link ${isDeceased ? 'member-deceased' : ''}`}
                                                                        title={isDeceased ? `${member.name || 'Member'} (Deceased)` : (member.name || 'Member')}
                                                                    >
                                                                        <MemberPhotoAvatar
                                                                            src={member.photo}
                                                                            alt={member.name || 'Member'}
                                                                            size={40}
                                                                            cacheKey={photoCacheKey}
                                                                        />
                                                                        <span className="member-avatar-name">
                                                                            {member.name || 'Unknown'}
                                                                            {isDeceased && <span className="member-deceased-indicator">†</span>}
                                                                        </span>
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </div>

            {/* Floating Add Button (Admin Only) */}
            {isAdmin && (
                <Link to="/families/add" className="fab-add-family" title="Add New Family">
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add Family</span>
                </Link>
            )}

            {/* Delete Family Confirmation Modal */}
            <Modal show={deleteModal.show} onHide={closeDeleteModal} centered className="delete-confirm-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faTrashAlt} />
                        Delete Family
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="delete-modal-message">
                        Are you sure you want to delete the <strong>{deleteModal.name}</strong> family?
                    </p>
                    <div className="delete-modal-warning">
                        This will also delete <strong>all family members</strong>. This action cannot be undone.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={closeDeleteModal} disabled={!!deletingFamilyId}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDeleteFamily} disabled={!!deletingFamilyId}>
                        {deletingFamilyId ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                Delete
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default FamilyList;
